import { useState } from 'preact/hooks';
import { closeSheet, showToast } from '@/state/ui';
import { createSchedule, updateSchedule } from '@/lib/mutations';
import type { Schedule } from '@/types';

interface Props {
  animalId: string;
  schedule?: Schedule;
}

/** Manual schedule editor (the agent is the primary way to create these,
 *  but everything must work without it). */
export function ScheduleSheet({ animalId, schedule }: Props) {
  const [stype, setStype] = useState<'interval' | 'times'>(schedule?.stype ?? 'interval');
  const [intervalMin, setIntervalMin] = useState(String(schedule?.interval_minutes ?? 60));
  const [timesStr, setTimesStr] = useState(
    (schedule?.times ?? ['08:00', '17:00']).map((t) => t.slice(0, 5)).join(', ')
  );
  const [windowed, setWindowed] = useState(!!schedule?.active_start);
  const [activeStart, setActiveStart] = useState(schedule?.active_start?.slice(0, 5) ?? '06:00');
  const [activeEnd, setActiveEnd] = useState(schedule?.active_end?.slice(0, 5) ?? '21:00');
  const [everyN, setEveryN] = useState(String(schedule?.feed_every_n_days ?? ''));
  const [amount, setAmount] = useState(schedule?.amount != null ? String(schedule.amount) : '');
  const [unit, setUnit] = useState(schedule?.unit ?? '');
  const [food, setFood] = useState(schedule?.food ?? '');

  const save = async () => {
    const times = timesStr
      .split(/[,\s]+/)
      .map((t) => t.trim())
      .filter((t) => /^\d{1,2}:\d{2}$/.test(t))
      .map((t) => (t.length === 4 ? `0${t}` : t));

    if (stype === 'interval' && (!+intervalMin || +intervalMin < 1)) {
      showToast({ kind: 'info', message: 'Interval needs minutes (e.g. 15)' });
      return;
    }
    if (stype === 'times' && times.length === 0) {
      showToast({ kind: 'info', message: 'Add at least one time like 08:00' });
      return;
    }

    const fields = {
      stype,
      interval_minutes: stype === 'interval' ? +intervalMin : null,
      times: stype === 'times' ? times : null,
      active_start: windowed && stype === 'interval' ? activeStart : null,
      active_end: windowed && stype === 'interval' ? activeEnd : null,
      feed_every_n_days: everyN && +everyN >= 2 ? +everyN : null,
      amount: amount ? +amount : null,
      unit: unit || null,
      food: food || null,
    };

    closeSheet();
    if (schedule) {
      await updateSchedule(schedule.id, fields);
      showToast({ kind: 'success', message: 'Schedule updated' });
    } else {
      await createSchedule({
        animal_id: animalId,
        label: 'feed',
        anchor_at: new Date().toISOString(),
        days_of_week: [0, 1, 2, 3, 4, 5, 6],
        fasting_dates: [],
        prep_notes: null,
        enabled: true,
        ...fields,
      });
      showToast({ kind: 'success', message: 'Schedule added — clock is running' });
    }
  };

  const disable = async () => {
    if (!schedule) return;
    closeSheet();
    await updateSchedule(schedule.id, { enabled: false });
    showToast({ kind: 'info', message: 'Schedule paused' });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-3)' }}>
      <div class="seg-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
        <button class={`seg ${stype === 'interval' ? 'is-on' : ''}`} onClick={() => setStype('interval')}>
          Every X minutes
        </button>
        <button class={`seg ${stype === 'times' ? 'is-on' : ''}`} onClick={() => setStype('times')}>
          Times of day
        </button>
      </div>

      {stype === 'interval' ? (
        <>
          <div>
            <label class="label" for="sched-interval">Every (minutes)</label>
            <input id="sched-interval" class="input" type="number" inputMode="numeric" min="1"
              value={intervalMin} onInput={(e) => setIntervalMin((e.target as HTMLInputElement).value)} />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-3)' }}>
            <span class={`switch ${windowed ? 'is-on' : ''}`} role="switch" aria-checked={windowed}
              onClick={() => setWindowed(!windowed)} />
            <span class="t-small">Daylight window only</span>
          </label>
          {windowed && (
            <div class="form-row2">
              <div>
                <label class="label" for="sched-start">From</label>
                <input id="sched-start" class="input" type="time" value={activeStart}
                  onInput={(e) => setActiveStart((e.target as HTMLInputElement).value)} />
              </div>
              <div>
                <label class="label" for="sched-end">Until</label>
                <input id="sched-end" class="input" type="time" value={activeEnd}
                  onInput={(e) => setActiveEnd((e.target as HTMLInputElement).value)} />
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          <div>
            <label class="label" for="sched-times">Times (comma-separated, 24h)</label>
            <input id="sched-times" class="input" placeholder="08:00, 12:30, 17:00" value={timesStr}
              onInput={(e) => setTimesStr((e.target as HTMLInputElement).value)} />
          </div>
          <div>
            <label class="label" for="sched-everyn">Feed every N days (blank = daily)</label>
            <input id="sched-everyn" class="input" type="number" inputMode="numeric" min="2" placeholder="e.g. 2"
              value={everyN} onInput={(e) => setEveryN((e.target as HTMLInputElement).value)} />
          </div>
        </>
      )}

      <div class="form-row2">
        <div>
          <label class="label" for="sched-amount">Amount</label>
          <input id="sched-amount" class="input" type="number" inputMode="decimal" step="any" placeholder="0.25"
            value={amount} onInput={(e) => setAmount((e.target as HTMLInputElement).value)} />
        </div>
        <div>
          <label class="label" for="sched-unit">Unit</label>
          <input id="sched-unit" class="input" placeholder="cup, ml, mice…" value={unit}
            onInput={(e) => setUnit((e.target as HTMLInputElement).value)} />
        </div>
      </div>
      <div>
        <label class="label" for="sched-food">Food</label>
        <input id="sched-food" class="input" placeholder="mealworm mix" value={food}
          onInput={(e) => setFood((e.target as HTMLInputElement).value)} />
      </div>

      <button class="btn btn--block" onClick={save}>
        {schedule ? 'Save changes' : 'Start schedule'}
      </button>
      {schedule && (
        <button class="btn btn--ghost btn--block" onClick={disable}>
          Pause this schedule
        </button>
      )}
    </div>
  );
}
